/**
 * 12 Days of Christmas Promotional Popup
 * Extends the base Popup class with:
 * - localStorage tracking (viewed days, last shown date)
 * - GA4 event tracking (with customer data for logged-in users)
 * - Cart attribute tracking (for order data)
 * - Reveal interaction
 * - Smart display logic (show newest day, once per day)
 */

class TwelveDaysPopup {
  constructor(config) {
    this.config = config;
    this.popupId = config.popupId;
    this.dayNumber = config.dayNumber;
    this.dayContent = config.dayContent;
    this.showDelay = config.showDelay || 500;
    this.customer = config.customer || {};

    this.overlay = document.getElementById(this.popupId);
    if (!this.overlay) {
      console.error('TwelveDaysPopup: Overlay not found:', this.popupId);
      return;
    }

    this.container = this.overlay.querySelector('.twelve-days-popup-container');
    this.closeButton = this.overlay.querySelector('.twelve-days-close');
    this.revealButton = this.overlay.querySelector('.twelve-days-reveal-button');
    this.revealState = this.overlay.querySelector('.twelve-days-reveal-state');
    this.revealedState = this.overlay.querySelector('.twelve-days-revealed-state');

    this.storageKey = 'twelvedays_2025';
    this.isRevealed = false;
    this.viewStartTime = null;
    this.revealTime = null;

    this.init();
  }

  init() {
    console.log('TwelveDaysPopup: Initializing for day', this.dayNumber);

    this.setupEventListeners();

    // Check if we should show the popup
    if (this.shouldShow()) {
      setTimeout(() => {
        this.show();
      }, this.showDelay);
    } else {
      console.log('TwelveDaysPopup: Not showing popup (already seen today or newer day seen)');
    }
  }

  setupEventListeners() {
    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Close on close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Reveal on reveal button click
    if (this.revealButton) {
      this.revealButton.addEventListener('click', () => this.reveal());
    }

    // Close on revealed image click
    const revealedImages = this.overlay.querySelectorAll('.twelve-days-image');
    revealedImages.forEach(image => {
      image.addEventListener('click', () => this.close());
      image.style.cursor = 'pointer';
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
        this.close();
      }
    });
  }

  shouldShow() {
    // TESTING MODE: Check if we should bypass restrictions
    // Enable by adding ?twelvedays_test=true to URL OR by setting testMode in config
    const urlParams = new URLSearchParams(window.location.search);
    const isTestMode = urlParams.get('twelvedays_test') === 'true' || this.config.testMode === true;

    if (isTestMode) {
      console.log('TwelveDaysPopup: TEST MODE ENABLED - Showing popup on every load');
      return true;
    }

    const data = this.getStorageData();
    const today = this.getTodayString();

    // Don't show if already shown today
    if (data.lastShownDate === today) {
      console.log('TwelveDaysPopup: Already shown today');
      return false;
    }

    // Don't show if user has already seen this day
    if (data.seenDays.includes(this.dayNumber)) {
      console.log('TwelveDaysPopup: User has already seen day', this.dayNumber);
      // Update last shown date so we don't check again today
      data.lastShownDate = today;
      this.setStorageData(data);
      return false;
    }

    return true;
  }

  show() {
    console.log('TwelveDaysPopup: Showing popup for day', this.dayNumber);

    this.overlay.classList.add('active');
    document.body.classList.add('twelve-days-popup-open');

    // Track view start time
    this.viewStartTime = Date.now();

    // Track in GA4
    this.trackGA4Event('twelve_days_popup_viewed', {
      day_number: this.dayNumber,
      popup_content: this.dayContent
    });

    // Trap focus
    this.trapFocus();
  }

  close() {
    console.log('TwelveDaysPopup: Closing popup');

    const timeOnPopup = this.viewStartTime ? (Date.now() - this.viewStartTime) / 1000 : 0;

    this.overlay.classList.remove('active');
    document.body.classList.remove('twelve-days-popup-open');

    // Mark this day as seen (so it won't show again)
    const data = this.getStorageData();
    data.lastSeenDay = this.dayNumber;
    if (!data.seenDays.includes(this.dayNumber)) {
      data.seenDays.push(this.dayNumber);
    }
    data.lastShownDate = this.getTodayString();
    this.setStorageData(data);

    // Update cart attributes
    this.updateCartAttributes(data);

    // Track close event
    this.trackGA4Event('twelve_days_popup_closed', {
      day_number: this.dayNumber,
      revealed: this.isRevealed,
      time_on_popup: timeOnPopup.toFixed(1)
    });
  }

  reveal() {
    if (this.isRevealed) return;

    console.log('TwelveDaysPopup: Revealing content');

    this.isRevealed = true;
    this.revealTime = Date.now();

    // Change background color to revealed state color
    if (this.container) {
      const revealedBgColor = this.container.getAttribute('data-revealed-bg-color');
      if (revealedBgColor) {
        this.container.style.backgroundColor = revealedBgColor;
      }
    }

    // Flip card animation for reveal image
    if (this.revealState) {
      // Start flipping the front face away
      this.revealState.classList.add('hiding');
    }

    if (this.revealedState) {
      // Show the revealed content with fade/scale (no flip)
      this.revealedState.style.display = 'flex';

      // Use requestAnimationFrame to ensure display change is applied before animation
      requestAnimationFrame(() => {
        this.revealedState.classList.add('showing');
      });

      // Clean up after animation completes (400ms + small buffer)
      setTimeout(() => {
        if (this.revealState) {
          this.revealState.style.display = 'none';
        }
      }, 450);
    }

    // Show terms & conditions after reveal
    const termsElement = this.overlay.querySelector('.twelve-days-terms');
    if (termsElement) {
      termsElement.style.display = 'block';
    }

    // Calculate time to reveal
    const timeToReveal = this.viewStartTime ? (this.revealTime - this.viewStartTime) / 1000 : 0;

    // Track reveal event
    this.trackGA4Event('twelve_days_popup_revealed', {
      day_number: this.dayNumber,
      time_to_reveal: timeToReveal.toFixed(1)
    });
  }

  reopen() {
    console.log('TwelveDaysPopup: Reopening popup');

    this.viewStartTime = Date.now();
    this.overlay.classList.add('active');
    document.body.classList.add('twelve-days-popup-open');

    // Track reopen event
    this.trackGA4Event('twelve_days_hero_reopened', {
      day_number: this.dayNumber
    });

    this.trapFocus();
  }

  // Storage helpers
  getStorageData() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('TwelveDaysPopup: Error reading localStorage', e);
    }

    // Return default structure
    return {
      lastSeenDay: null,
      seenDays: [],
      lastShownDate: null
    };
  }

  setStorageData(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      console.error('TwelveDaysPopup: Error writing to localStorage', e);
    }
  }

  getTodayString() {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  // GA4 tracking - supports both GTM (dataLayer) and GA4 (gtag)
  trackGA4Event(eventName, params = {}) {
    // Add customer data if logged in
    if (this.customer.loggedIn && this.customer.id) {
      params.customer_id = this.customer.id;
      params.user_id = this.customer.id;

      if (this.customer.email) {
        params.customer_email = this.customer.email;
      }
    }

    try {
      // Try GTM dataLayer first (Shopify pixels usually load this)
      if (window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...params
        });
        console.log('TwelveDaysPopup: Event pushed to GTM dataLayer:', eventName, params);
        return;
      }

      // Fallback to GA4 gtag
      if (typeof gtag === 'function') {
        gtag('event', eventName, params);
        console.log('TwelveDaysPopup: Event pushed to GA4:', eventName, params);
        return;
      }

      // Neither available
      console.log('TwelveDaysPopup: No analytics available, skipping event:', eventName);
    } catch (e) {
      console.error('TwelveDaysPopup: Error tracking event', e);
    }
  }

  // Cart attributes tracking
  updateCartAttributes(storageData) {
    const attributes = {
      'twelve_days_seen': JSON.stringify(storageData.seenDays),
      'twelve_days_last': storageData.lastSeenDay.toString(),
      'twelve_days_last_date': storageData.lastShownDate
    };

    console.log('TwelveDaysPopup: Updating cart attributes', attributes);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes })
    })
      .then(response => response.json())
      .then(data => {
        console.log('TwelveDaysPopup: Cart attributes updated', data.attributes);
      })
      .catch(error => {
        console.error('TwelveDaysPopup: Error updating cart attributes', error);
      });
  }

  // Focus trap for accessibility
  trapFocus() {
    if (!this.container) return;

    const focusableElements = this.container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the close button initially
    if (this.closeButton) {
      this.closeButton.focus();
    }

    // Trap focus within popup
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    };

    this.container.addEventListener('keydown', handleTabKey);
  }
}

// Initialize when config is available
document.addEventListener('DOMContentLoaded', () => {
  if (typeof window.twelveDaysConfig !== 'undefined') {
    console.log('TwelveDaysPopup: Initializing with config', window.twelveDaysConfig);
    window.twelveDaysPopupInstance = new TwelveDaysPopup(window.twelveDaysConfig);
  } else {
    console.log('TwelveDaysPopup: No config found, skipping initialization');
  }
});

// Export for global access (e.g., for reopen functionality)
window.TwelveDaysPopup = TwelveDaysPopup;
